import {
  EnquiryHandle,
  IServerSideGetRowsParams,
  ServerSideCriteria,
  useServerEnquiry,
  Column,
} from '@access-erp/common';
import { ConfirmationModal, FlexProps, ModalType } from '@access-erp/ui-components';
import { Guid } from 'guid-typescript';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';
import { Forms, SalesOrderByBillingCycleFilterInput } from '@/graphql/contracts/_generated_/contracts';
import { UserRole } from '@/config/roles.config';
import { useGetInitialFiltersSet } from '@/components/common/EnquiriesScreen/hooks/useGetInitialFiltersSet';
import { mergeAdvancedFilters } from '@/components/common/EnquiriesScreen/utils/advancedFilterHelpers';
import { GetPaginatedFilteredSalesOrdersVariables }
  from '@/graphql/contracts/_generated_/getPaginatedFilteredSalesOrders.query';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { CONTRACTS_PER_PAGE } from '@/config/common.config';
import { SalesOrderWithId } from '@/utils/data-helpers/types';
import { useColumns, UseTransactionTableColumnsReturn } from './hooks/useColumns';
import { billingBCPath } from '@/utils/path.utlis';
import { useDefaultGridFilter } from './columns/useDefaultGridFilter';
import useGetSalesOrders from './hooks/useGetSalesOrders';
import useGetBCSalesOrders from './hooks/useGetBCSalesOrders';
import { SOActionEnum, useRightPanel } from './hooks/useRightPanel';
import PredictionTableModal from './components/PredictionTableModal/PredictionTableModal';
import { SalesOrderPayloadModal } from './components/SalesOrderPayload/SalesOrderPayloadModal';
import { ActionConfirmationModalBody } from './components/ActionConfirmationText/ActionConfirmationModalBody';
import { AbandonSalesOrderModal } from './components/AbandonSalesOrderModal';

export interface UseTableColumns extends UseTransactionTableColumnsReturn {
  goToSalesOrdersBySearch: (searchText?: string) => void;
}

export const SalesOrderComponent = () => {
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [selectedPayloadRow, setSelectedPayloadRow] = useState<SalesOrderWithId | undefined>(undefined);
  const [isPayloadModalVisible, setIsPayloadModalVisible] = useState(false);
  const gridRef = useRef<EnquiryHandle<SalesOrderWithId>>(null);
  const { pathname, search } = useLocation();
  const isBillingCyclesPath = pathname.includes(billingBCPath);
  
  // Extract query params for grid key - forces remount when params change
  const urlParams = useMemo(() => new URLSearchParams(search), [search]);
  const billingCycleIdParam = urlParams.get('billingCycleId');
  const salesOrderIdParam = urlParams.get('salesOrderId');
  const gridKey = `${billingCycleIdParam || ''}-${salesOrderIdParam || ''}`;
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  const { getSalesOrders } = useGetSalesOrders();
  const { getBCSalesOrders } = useGetBCSalesOrders();
  const { getSavedFilterIfAny, savedFilters } = useGetInitialFiltersSet();
  const { defaultFilters, subMenuBillingPage, extractSubMenuStatuses } = useDefaultGridFilter();
  const getBillingSO = isBillingCyclesPath ? getBCSalesOrders : getSalesOrders;

  const mergedFilters = mergeAdvancedFilters(defaultFilters, savedFilters);

  useEffect(() => {
    const getInitialData = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getInitialData();
  }, [getUserRole]);

  const { headerColumns, goToSalesOrdersBySearch } = useColumns(
    isBillingCyclesPath
  ) as UseTableColumns;

  const baseHeaderColumns = headerColumns as any[];
  const finalHeaderColumns = useMemo(() => {
    const payloadColumn: Column<any> = {
      headerName: '',
      field: '__payload__',
      pinned: 'left',
      name: 'payload',
      caption: 'Payload',
      width: 120,
      cellRenderer: ({ data }: any) => {
        return (
          <a
            className="btn btn-link"
            style={{ textDecoration: 'none' }}
            onClick={(e) => {
              e.preventDefault();
              setSelectedPayloadRow(data);
              setIsPayloadModalVisible(true);
            }}
          >
            View Payload
          </a>
        );
      },
      suppressMenu: true,
      sortable: false,
      filter: false,
      resizable: false,
      filterable: false,
      groupable: false,
      suppressAdvancedFilter: true,
      suppressMovable: true,
      disableRowGroup: true,
    } as any;

    return [payloadColumn, ...baseHeaderColumns];
  }, [baseHeaderColumns]);

  const enquiry = useServerEnquiry<
  SalesOrderWithId,
  GetPaginatedFilteredSalesOrdersVariables['filter']
  >({
    gridRef,
    headerColumns: finalHeaderColumns,
    initialFilter: mergedFilters,
  });

  const {
    tabsConfiguration,
    closePredictionTableModal,
    isVisiblePredictionModal,
    isVisibleActionConfirmModal,
    isVisibleSOPayloadModal: isVisibleTroubleshootingModal,
    setIsVisibleSOPayloadModal: setIsVisibleTroubleshootingModal,
    closeActionConfirmModal,
    confirmResendSalesOrders,
    action,
    isVisibleAbandonModal,
    setIsVisibleAbandonModal,
    handleSuccessAction,
  } = useRightPanel({
    gridRef: gridRef.current ?? undefined,
    selectedHeaderRows: enquiry.selectedHeaderRows,
    userRole: userRole as UserRole,
  });

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;

  const getData = async (
    params: IServerSideGetRowsParams<SalesOrderWithId>,
    serverSideCriteria: ServerSideCriteria,
  ) => {
    await getSavedFilterIfAny(Forms.Billing, params);
    const statusSelected = enquiry.serverSide?.current?.customFilters?.status?.formattedStatus;

    // Read billingCycleId and salesOrderId from URL query parameters
    const billingCycleIdFilter = billingCycleIdParam
      ? { billingCycleId: { eq: billingCycleIdParam } }
      : undefined;
    const salesOrderIdFilter = salesOrderIdParam
      ? { salesOrderID: { eq: salesOrderIdParam } }
      : undefined;
    const urlFilter = salesOrderIdFilter || billingCycleIdFilter;
    const filter = urlFilter !== undefined
      ? urlFilter as SalesOrderByBillingCycleFilterInput
      : {
        summary: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        status:
        subMenuBillingPage && !statusSelected
          ? { formattedStatus: { containsValues: extractSubMenuStatuses() } }
          : enquiry.serverSide?.current?.customFilters?.status,
        ...serverSideCriteria.groupingFilters,
      };

    const salesOrdersData = await getBillingSO({
      after: serverSideCriteria.nextPageCursor,
      first: CONTRACTS_PER_PAGE,
      filter,
      sort: serverSideCriteria.userSetSorting,
      group: serverSideCriteria.groupByColumn as any,
    });

    setPageIsLoading(false);

    const searchIsCleared = enquiry.serverSide?.current?.search === '';

    if (enquiry.serverSide?.current?.search || searchIsCleared) {
      goToSalesOrdersBySearch(enquiry.serverSide?.current?.search);
    }

    return {
      items:
        salesOrdersData?.items?.map((el) => ({
          ...el,
          id: el?.salesOrderID && !Guid.parse(el.salesOrderID).isEmpty() ? el.salesOrderID : Guid.create().toString(),
        })) ?? [], pageInfo: {
        hasNextPage: salesOrdersData?.pageInfo.hasNextPage,
        hasPreviousPage: salesOrdersData?.pageInfo.hasPreviousPage,
        endCursor: salesOrdersData?.pageInfo?.endCursor,
        startCursor: salesOrdersData?.pageInfo?.startCursor,
      },
    };
  };

  return (
    <>
            <ContractsEnquiriesScreen
              key={gridKey}
              ref={gridRef}
              enquiry={enquiry}
        filterForm={isBillingCyclesPath ? Forms.BillingByBillingCycles : Forms.Billing}
        formName={'SalesOrders'}
        headerProps={{
          primaryTitle: 'Sales Orders',
          secondaryTitle: 'List',
          searchPlaceholder: 'Start typing Contract Number or Account Name',
          showExportAll: userRole === UserRole.FULL_ACCESS || userRole === UserRole.ADMIN,
          showExportCurrent: false,
          search: getSavedSearch?.()?.savedSearch,
        }}
        detailProps={{
          exportFileName: 'Sales Orders',
          rowGroupPanelShow: 'always',
          getData: getData as any,
          pageSize: CONTRACTS_PER_PAGE,
          suppressRowClickSelection: false,
          loading: pageIsLoading,
        }}
        rightPanelTabs={tabsConfiguration}
      />

      {isVisiblePredictionModal && (
        <PredictionTableModal
          closeModal={closePredictionTableModal}
          isVisible={isVisiblePredictionModal}
        />
      )} *

      {(isPayloadModalVisible || isVisibleTroubleshootingModal) &&
        (selectedPayloadRow || enquiry.selectedHeaderRows?.[0]?.data) && (
        <SalesOrderPayloadModal
          handleClose={() => {
            setIsPayloadModalVisible(false);
            setIsVisibleTroubleshootingModal(false);
            setSelectedPayloadRow(undefined);
          }}
          isVisible={isPayloadModalVisible || isVisibleTroubleshootingModal}
          selectedRow={(selectedPayloadRow || enquiry.selectedHeaderRows?.[0]?.data)!}
          isBillingCyclesPath={isBillingCyclesPath}
        />
      )}

      <ConfirmationModal
        open={isVisibleActionConfirmModal}
        onClose={closeActionConfirmModal}
        onConfirm={confirmResendSalesOrders}
        confirmButtonText="Confirm"
        title="Please confirm you wish to continue"
        type={ModalType.Warning}
        footerAlign={FlexProps['space-between']}
      >
        <ActionConfirmationModalBody action={action ?? SOActionEnum.RESEND} />
      </ConfirmationModal>

      {isVisibleAbandonModal && enquiry.selectedHeaderRows?.[0]?.data && (
        <AbandonSalesOrderModal
          isOpen={isVisibleAbandonModal}
          onClose={() => setIsVisibleAbandonModal(false)}
          salesOrder={enquiry.selectedHeaderRows[0].data}
          onSuccess={handleSuccessAction}
        />
      )}
    </>
  );
};
